import {Fragment, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Box,
    Checkbox,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    withStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {AgentStatus} from './AgentStatus';
import API from '../../APIs/API';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import {Alert, Color} from '@material-ui/lab';
import RootStore from '../../Core/mobx/RootStore';
import {InternalUserRole} from '@amplify/athena-models';

export const LeadAssignmentPool = observer(({type}: {type: 'AA' | 'DEL'}) => {
    const GROUPS = {
        '1': 'Tier 1+2',
        '2': 'Tier 2 only',
        '3': 'Tier 3+4 (BDS + Alt-Mon)',
    };
    const [msg, setMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<Color>('error');
    const [showDialog, setShowDialog] = useState<null | 'save' | 'reset'>(null);
    const [loading, setLoading] = useState(true);
    const [agentStatuses, setAgentStatuses] = useState<Array<AgentStatus> | null>(null);
    const [canSave, setCanSave] = useState(false);

    const userAdminStore = RootStore.userAdminStore;
    const internalUsers = userAdminStore.internalUsers;

    const getInternalUsers = async () => {
        console.log('Getting internal users');
        userAdminStore.getInternalUsers();
    };

    useEffect(() => {
        getInternalUsers();
    }, []);

    const styles = (theme: any) => ({
        root: {
            margin: 0,
            padding: theme.spacing(2),
        },
        closeButton: {
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
        },
    });

    const dialogStyles = {
        content: {
            minWidth: 450,
            paddingTop: 0,
        },
        buttonContainer: {
            marginTop: 30,
            display: 'flex',
            justifyContent: 'center',
        },
    };

    // @ts-ignore
    const DialogTitle = withStyles(styles)((props) => {
        // @ts-ignore
        const {children, classes, onClose, ...other} = props;
        return (
            <MuiDialogTitle disableTypography className={classes.root} {...other}>
                <Typography variant="h6">{children}</Typography>
                {onClose ? (
                    <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                ) : null}
            </MuiDialogTitle>
        );
    });

    const DialogContent = withStyles((theme) => ({
        root: {
            padding: theme.spacing(2),
        },
    }))(MuiDialogContent);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        API.Call<AgentStatus[]>('/v2/lead-assignment/status', 'PUT', []).then((data) => {
            setLoading(false);
            if (data) {
                timeoutId = setTimeout(
                    () => {
                        setCanSave(false);
                        setAlertSeverity('warning');
                        setMessage('To ensure data accuracy, please refresh this page to submit changes.');
                    },
                    1000 * 60 * 10,
                ); // 10 min

                setAgentStatuses(data.sort((a, b) => a.firstName.localeCompare(b.firstName)));
            } else {
                setAlertSeverity('error');
                setMessage('Unable to display agent statuses');
            }
        });
        return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => validateData(agentStatuses), [agentStatuses]);

    const validateData = (data: Array<AgentStatus> | null) => {
        // ensure all enabled agents have tiers selected
        const invalid = data?.filter((d) => d.active && !d.group);
        setCanSave(invalid?.length === 0);
    };

    const handleStatusChange = (event: any, hubspotId: string) => {
        if (agentStatuses) {
            const ind = agentStatuses?.findIndex((a) => a.hubspotId === hubspotId);
            if (ind >= 0) {
                const agentStatusesClone = [...agentStatuses];
                if (type === 'AA') agentStatusesClone[ind].active = event.target.checked;
                else agentStatusesClone[ind].delActive = event.target.checked;
                setAgentStatuses(agentStatusesClone);
            }
        }
    };

    const handleAdjustChange = (event: any, hubspotId: string) => {
        if (agentStatuses) {
            const ind = agentStatuses?.findIndex((a) => a.hubspotId === hubspotId);
            if (ind >= 0) {
                const agentStatusesClone = [...agentStatuses];
                agentStatusesClone[ind].dailyAdjustment = event.target.value;
                setAgentStatuses(agentStatusesClone);
            }
        }
    };

    const handleGroupChange = (event: any, hubspotId: string) => {
        if (agentStatuses) {
            const ind = agentStatuses?.findIndex((a) => a.hubspotId === hubspotId);
            if (ind >= 0) {
                const agentStatusesClone = [...agentStatuses];
                agentStatusesClone[ind].group = event.target.value;
                setAgentStatuses(agentStatusesClone);
            }
        }
    };

    const submitStatusUpdates = () => {
        setShowDialog(null);
        setLoading(true);
        API.Call<AgentStatus[]>('/v2/lead-assignment/status', 'PUT', agentStatuses).then((data) => {
            setLoading(false);
            if (data) {
                setAlertSeverity('success');
                setMessage('Successfully updated agent statuses');
                setAgentStatuses(data.sort((a, b) => a.firstName.localeCompare(b.firstName)));
            } else {
                setAlertSeverity('error');
                setMessage('Error updating agent statuses');
            }
        });
    };

    const resetLeadCounters = () => {
        setShowDialog(null);
        API.Call<{status: string}>(`/v2/lead-assignment/reset/${type}`, 'GET').then((data) => {
            if (data?.status === 'success') {
                setAlertSeverity('success');
                setMessage('Successfully reset agent lead counters');
            } else {
                setAlertSeverity('error');
                setMessage('Error resetting agent lead counters');
            }
        });
    };

    const openSaveDialog = () => {
        setShowDialog('save');
    };

    const openResetDialog = () => {
        setShowDialog('reset');
    };

    const closeDialog = () => {
        setShowDialog(null);
    };

    return (
        <Fragment>
            <Snackbar
                open={msg != ''}
                autoHideDuration={4000}
                onClose={() => {
                    setMessage('');
                }}
            >
                <Alert
                    onClose={() => {
                        setMessage('');
                    }}
                    severity={alertSeverity}
                >
                    {msg}
                </Alert>
            </Snackbar>
            <Dialog open={showDialog === 'save'} onClose={closeDialog}>
                {/*
                // @ts-ignore */}
                <DialogTitle onClose={closeDialog}>Submit agent status updates?</DialogTitle>
                <DialogContent dividers style={dialogStyles.content}>
                    <div>
                        <Box p={3}>
                            <Typography>
                                Toggling the "Enabled?" field for an agent will control whether new leads can be
                                automatically assigned to the associated agent. Are you sure you would like to submit
                                these updates?
                            </Typography>
                        </Box>
                    </div>
                    <div style={dialogStyles.buttonContainer}>
                        <Button autoFocus color="primary" variant="contained" onClick={submitStatusUpdates}>
                            Submit
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={showDialog === 'reset'} onClose={closeDialog}>
                {/*
                // @ts-ignore */}
                <DialogTitle onClose={closeDialog}>Reset agent lead counters?</DialogTitle>
                <DialogContent dividers style={dialogStyles.content}>
                    <div>
                        <Box p={3}>
                            <Typography>
                                Agent lead counters are used to ensure lead volume is distributed evenly amongst agents
                                in the assignment pool. Are you sure you would like to set the agent lead counters to 0?
                            </Typography>
                        </Box>
                    </div>
                    <div style={dialogStyles.buttonContainer}>
                        <Button autoFocus color="primary" variant="contained" onClick={resetLeadCounters}>
                            Reset Counters
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            {loading && <div>Loading... please wait...</div>}
            {!loading && agentStatuses && (
                <Grid container spacing={3}>
                    <Grid item xs={1}>
                        <Button variant="contained" color="primary" disabled={!canSave} onClick={openSaveDialog}>
                            Save
                        </Button>
                    </Grid>
                    <Grid item xs={2}>
                        <Button variant="contained" color="primary" onClick={openResetDialog}>
                            Reset Counters
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <TableContainer component={Paper}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Hubspot Owner ID</TableCell>
                                        <TableCell>Agent Name</TableCell>
                                        {type == 'AA' && (
                                            <>
                                                <TableCell>Daily Adjustment</TableCell>
                                                <TableCell>Group</TableCell>
                                            </>
                                        )}
                                        <TableCell>Enabled?</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {agentStatuses?.map((a, i) => (
                                        <>
                                            {((type == 'AA' ? a.active : a.delActive) ||
                                                !!internalUsers?.find(
                                                    (iu) =>
                                                        iu.email === a.contactEmail &&
                                                        iu.roles.includes(InternalUserRole.AGENT),
                                                )) && (
                                                <TableRow key={`agent-row-${i}`}>
                                                    <TableCell key={`agent-id-${i}`} data-testid={`agent-id-${i}`}>
                                                        {a.hubspotId}
                                                    </TableCell>
                                                    <TableCell
                                                        key={`agent-name-${i}`}
                                                        data-testid={`agent-name-${i}`}
                                                    >{`${a.firstName} ${a.lastName}`}</TableCell>
                                                    {type == 'AA' && (
                                                        <>
                                                            <TableCell
                                                                key={`agent-adjust-cell-${i}`}
                                                                data-testid={`agent-adjust-cell-${i}`}
                                                            >
                                                                <Select
                                                                    key={`agent-adjust-${i}`}
                                                                    data-testid={`agent-adjust-${i}`}
                                                                    value={a.dailyAdjustment}
                                                                    label="Count"
                                                                    onChange={(e) => handleAdjustChange(e, a.hubspotId)}
                                                                >
                                                                    <MenuItem value={6}>+6</MenuItem>
                                                                    <MenuItem value={5}>+5</MenuItem>
                                                                    <MenuItem value={4}>+4</MenuItem>
                                                                    <MenuItem value={3}>+3</MenuItem>
                                                                    <MenuItem value={2}>+2</MenuItem>
                                                                    <MenuItem value={1}>+1</MenuItem>
                                                                    <MenuItem value={0}>0</MenuItem>
                                                                    <MenuItem value={-1}>-1</MenuItem>
                                                                    <MenuItem value={-2}>-2</MenuItem>
                                                                    <MenuItem value={-3}>-3</MenuItem>
                                                                    <MenuItem value={-4}>-4</MenuItem>
                                                                    <MenuItem value={-5}>-5</MenuItem>
                                                                    <MenuItem value={-6}>-6</MenuItem>
                                                                </Select>
                                                            </TableCell>
                                                            <TableCell
                                                                key={`agent-tiers-cell-${i}`}
                                                                data-testid={`agent-tiers-cell-${i}`}
                                                            >
                                                                <Select
                                                                    key={`agent-tiers-${i}`}
                                                                    data-testid={`agent-tiers-${i}`}
                                                                    value={a.group}
                                                                    label="Group"
                                                                    onChange={(e) => handleGroupChange(e, a.hubspotId)}
                                                                >
                                                                    {Object.entries(GROUPS).map(([k, v]) => (
                                                                        <MenuItem value={k}>{v}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </TableCell>
                                                        </>
                                                    )}
                                                    <TableCell
                                                        key={`agent-enabled-cell-${i}`}
                                                        data-testid={`agent-enabled-cell-${i}`}
                                                    >
                                                        <Checkbox
                                                            key={`agent-enabled-${i}`}
                                                            data-testid={`agent-enabled-${i}`}
                                                            checked={type === 'AA' ? a.active : a.delActive}
                                                            onChange={(e) => handleStatusChange(e, a.hubspotId)}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                    <Grid item xs={1}>
                        <Button variant="contained" color="primary" disabled={!canSave} onClick={openSaveDialog}>
                            Save
                        </Button>
                    </Grid>
                    <Grid item xs={2}>
                        <Button variant="contained" color="primary" onClick={openResetDialog}>
                            Reset Counters
                        </Button>
                    </Grid>
                </Grid>
            )}
            {!loading && !agentStatuses && <div>You do not have permission to edit agent lead pools.</div>}
        </Fragment>
    );
});
